import React, { useEffect, useRef, useState } from 'react';
import { authService } from '../../services/authService';
import { current as currentRef, clear as clearRef } from '../../lib/referralRef';

// Вход по одноразовой ссылке из письма-приглашения в организацию:
// /login-link#t=<токен>. Приглашённый входит сразу, без второго круга «код на
// почту» (владелец 24.09.2026: одноразовая, сутки, неподбираемая).
//
// Токен приходит во фрагменте (#), а не в ?query: фрагмент браузер на сервер не
// шлёт, и живая ссылка входа не оседает в логах nginx и traefik. Из адресной
// строки он стирается сразу, а до гашения держится в sessionStorage вкладки —
// иначе перезагрузка на шаге с отметками согласия показала бы «ссылка
// устарела», хотя она цела.
//
// 🔴 Согласие. С 24.09 новая учётная запись заводится только с двумя отметками
// (ClientAuthPage). Приглашённый, у которого учётки ещё нет, их не ставил,
// поэтому сервер отвечает 428, ссылку не гасит, и здесь появляются те же две
// отметки. Вошедшему раньше они не показываются — он их уже ставил.

export const LOGIN_LINK_PATH = '/login-link';
const STORE_KEY = 'newton.loginLink';

export function isLoginLinkPath(pathname) {
  return (pathname || '').toLowerCase().replace(/\/+$/, '') === LOGIN_LINK_PATH;
}

const CARD = 'w-full max-w-[400px] bg-paper-card rounded-[20px] p-7 flex flex-col gap-4';
const PRIMARY = 'rounded-control py-[13px] text-[14px] font-semibold text-center bg-paper-accent '
  + 'text-white hover:bg-paper-accent-hover disabled:bg-paper-bg disabled:text-paper-hint transition-colors';

// Забирает токен из фрагмента и сразу чистит адрес; без фрагмента — из хранилища вкладки.
function takeToken() {
  const hash = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  const fromHash = hash.get('t');
  if (fromHash) {
    try { sessionStorage.setItem(STORE_KEY, fromHash); } catch { /* приватный режим */ }
    window.history.replaceState({}, '', window.location.pathname);
    return fromHash;
  }
  try { return sessionStorage.getItem(STORE_KEY); } catch { return null; }
}

function forgetToken() {
  try { sessionStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
}

const LoginLinkPage = ({ onAuth }) => {
  // checking → consent (новая учётка) → вход; failed — ссылка не пустила.
  const [stage, setStage] = useState('checking');
  const [error, setError] = useState('');
  const [offer, setOffer] = useState(false);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const token = useRef(null);
  const started = useRef(false);

  const redeem = async (withConsent) => {
    setBusy(true);
    setError('');
    try {
      const data = await authService.redeemLoginLink(token.current, withConsent, currentRef());
      // Ссылка на сервере уже погашена. Если следующий шаг (проверка ключа)
      // упадёт по сети, человек не должен увидеть «ссылка устарела» и потерять
      // вход: ключ сохраняем сразу, ошибку проверки объясняем отдельно
      // (ревью Fable 25.09).
      forgetToken();
      clearRef();
      try {
        await onAuth(data.token);
      } catch (_) {
        // Ключ кладём туда же, где его ищет старт приложения (index.js):
        // обновление страницы поднимет сессию обычным путём.
        try { localStorage.setItem('authToken', data.token); } catch (__) { /* приватный режим */ }
        setError('Вход выполнен, но страница не обновилась — обновите её.');
        setStage('failed');
      }
      return;
    } catch (err) {
      if (err.code === 'consent_required') {
        setStage('consent');
      } else {
        forgetToken();
        setError(err.message || 'Ссылка устарела или уже использована.');
        setStage('failed');
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    // Один запрос на загрузку: второй по той же ссылке у существующей учётки
    // получил бы 410 и показал бы отказ поверх уже случившегося входа.
    if (started.current) return;
    started.current = true;
    token.current = takeToken();
    if (!token.current) {
      setError('Ссылка устарела или уже использована.');
      setStage('failed');
      return;
    }
    redeem(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = (text, hint) => (
    <div>
      <div className="font-display text-[24px] font-extrabold tracking-[-0.03em] text-paper-ink">
        {text}
      </div>
      {hint && <div className="mt-[6px] text-[13px] leading-[1.5] text-paper-ink-2">{hint}</div>}
    </div>
  );

  const renderConsent = () => (
    <>
      {title('Вход по приглашению', 'Учётная запись заведётся при входе. Осталось отметить согласие.')}

      <label className="flex items-start gap-3 text-[12px] leading-[1.5] text-paper-ink-2 cursor-pointer">
        <input
          type="checkbox"
          data-testid="link-offer"
          checked={offer}
          onChange={(e) => setOffer(e.target.checked)}
          className="mt-[2px] w-4 h-4 accent-paper-accent"
        />
        <span>
          Я принимаю условия{' '}
          <a href="/oferta" target="_blank" rel="noreferrer"
             className="font-semibold text-paper-accent hover:underline">публичной оферты</a>.
        </span>
      </label>

      <label className="flex items-start gap-3 text-[12px] leading-[1.5] text-paper-ink-2 cursor-pointer">
        <input
          type="checkbox"
          data-testid="link-consent"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-[2px] w-4 h-4 accent-paper-accent"
        />
        <span>
          Я даю{' '}
          <a href="/consent" target="_blank" rel="noreferrer"
             className="font-semibold text-paper-accent hover:underline">
            согласие на обработку персональных данных</a>{' '}
          в соответствии с{' '}
          <a href="/privacy" target="_blank" rel="noreferrer"
             className="font-semibold text-paper-accent hover:underline">
            Политикой конфиденциальности</a>.
        </span>
      </label>

      <button
        type="button"
        data-testid="link-continue"
        disabled={!offer || !consent || busy}
        onClick={() => redeem(true)}
        className={PRIMARY}
      >
        {busy ? 'Входим…' : 'Войти'}
      </button>
    </>
  );

  const renderFailed = () => (
    <>
      {title('Ссылка не сработала')}
      <p data-testid="link-error" className="text-[13px] leading-[1.5] text-paper-accent">{error}</p>
      <p className="text-[13px] leading-[1.5] text-paper-ink-2">
        Войдите по коду: пришлём его на адрес, на который пришло приглашение.
      </p>
      {/* Полная перезагрузка на корень: там обычный вход по коду. */}
      <a href="/" data-testid="link-to-code" className={PRIMARY}>Войти по коду</a>
    </>
  );

  return (
    <div className="flex items-center justify-center py-16 px-4">
      <div className={CARD}>
        {stage === 'checking' && title('Входим…', 'Проверяем ссылку из письма.')}
        {stage === 'consent' && renderConsent()}
        {stage === 'failed' && renderFailed()}
      </div>
    </div>
  );
};

export default LoginLinkPage;
